import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import {
	ActiveAuthorizationProperties,
	AddAuthorizationProperties,
	AllocatedAuthorizationDetailsProperties,
	AllocationDataProperties,
	AllocationPageDataProperties,
	AuthorizationDetailsProperties,
	AuthorizationProperties,
	CalculatedHoursRangeProperties,
	GetAllocatedHoursDetailProperties,
	GetAllocationPageDataProperties,
	GetAuthorizationReportProperties,
	GetAvailableHoursProperties,
	GetCalculatedHoursRangeProperties,
	GetInsuranceAuthorizationsProperties,
	GetUtilizationReportProperties,
	InsuranceAuthorizationSessionTypeProperties,
	InsuranceAuthorizationTypeProperties,
	InsuranceAuthorizationsProperties,
	UpdateAllocatedHoursProperties,
	UpdateInsuranceAuthorization,
	UserAllocatedHoursDetailProperties,
	UserUtilizationProperties,
} from '../../API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { ConfirmationResponseProperties } from '../../API/ClientAPIHelpers/clientProperties';

interface InsuranceAuthorizationStateProperties {
	allInsuranceAuthorizations: InsuranceAuthorizationsProperties;
	allocatedHoursDetails: AuthorizationDetailsProperties[];
	allocationData: AllocationDataProperties[];
	allocationPageData: AllocationPageDataProperties | null;
	allocationPageLoading: boolean;
	authorizationDetails: ActiveAuthorizationProperties[] | null;
	availableHoursDetails: AuthorizationDetailsProperties[];
	error: ErrorProperties;
	insuranceAuthorizationSessionTypes: InsuranceAuthorizationSessionTypeProperties[];
	insuranceAuthorizationTypes: InsuranceAuthorizationTypeProperties[];
	isUpdatingAllocation: boolean;
	loading: boolean;
}

const initialState: InsuranceAuthorizationStateProperties = {
	loading: false,
	authorizationDetails: null,
	allocationData: [],
	allocatedHoursDetails: [],
	availableHoursDetails: [],
	insuranceAuthorizationSessionTypes: [],
	allInsuranceAuthorizations: {
		query: null,
	},
	insuranceAuthorizationTypes: [],
	error: {
		status: 0,
		title: '',
	},
	allocationPageData: null,
	allocationPageLoading: false,
	isUpdatingAllocation: false,
};

export const getInsuranceAuthorizationTypes = createAsyncThunk('/client/insurance/types', async (_, thunkAPI) => {
	const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizationTypes();

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as InsuranceAuthorizationTypeProperties[];
});

export const getUsersAllocationData = createAsyncThunk(
	'/authorization/removal/allocated-hours',
	async (data: { clientId: string; userIds: string[] }, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getUsersAllocationData(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AllocationDataProperties[];
	},
);

export const getInsuranceAuthorizationSessionTypes = createAsyncThunk(
	'/authorization/session-types',
	async (type: string | undefined, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizationSessionTypes(type);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getHourPerWeekInsuranceAuthorizationsData = createAsyncThunk(
	'/authorization/hour-per-week/data',
	async (data: GetCalculatedHoursRangeProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizationPerWeekData(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as CalculatedHoursRangeProperties;
	},
);

export const getActiveInsuranceAuthorizations = createAsyncThunk(
	'/authorization/active',
	async (data: { clientId: string; userId?: string }, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getActiveInsuranceAuthorizations(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ActiveAuthorizationProperties[];
	},
);

export const getInsuranceAuthorizations = createAsyncThunk(
	'/authorization/all',
	async (data: GetInsuranceAuthorizationsProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizations(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as InsuranceAuthorizationsProperties;
	},
);

export const saveInsuranceAuthorizations = createAsyncThunk(
	'/authorization/save',
	async ({ clientId, data }: { clientId: string; data: AddAuthorizationProperties }, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.saveInsuranceAuthorizations(clientId, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const saveAssessmentInsuranceAuthorizations = createAsyncThunk(
	'/authorization/save/assessment',
	async ({ clientId, data }: { clientId: string; data: AuthorizationProperties }, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.saveAssessmentInsuranceAuthorizations(clientId, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ConfirmationResponseProperties;
	},
);

export const saveTreatmentInsuranceAuthorizations = createAsyncThunk(
	'/authorization/save/treatment',
	async ({ clientId, data }: { clientId: string; data: AuthorizationProperties }, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.saveTreatmentAuthorizations(clientId, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ConfirmationResponseProperties;
	},
);

export const getAvailableHours = createAsyncThunk(
	'/authorization/available-hours/details',
	async (data: GetAvailableHoursProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getAvailableHours(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AuthorizationDetailsProperties[];
	},
);

export const getAllocatedHoursDetailsUser = createAsyncThunk(
	'/authorization/allocated-hours/details/user',
	async (data: UserAllocatedHoursDetailProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getAllocatedHoursDetailsUser(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AuthorizationDetailsProperties[];
	},
);

export const getAllocatedHoursDetails = createAsyncThunk(
	'/authorization/allocated-hours/details',
	async (data: GetAllocatedHoursDetailProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getAllocatedHoursDetails(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AllocatedAuthorizationDetailsProperties[];
	},
);

export const getUserUtilizationDetails = createAsyncThunk(
	'/authorization/user/utilization/details',
	async (data: GetAllocatedHoursDetailProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getUserUtilizationDetails(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserUtilizationProperties[];
	},
);

export const updateAllocatedHours = createAsyncThunk(
	'/authorization/allocated-hours/update',
	async (data: UpdateAllocatedHoursProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.updateAllocatedHours(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateAuthorization = createAsyncThunk(
	'/authorization/edit/update',
	async (data: UpdateInsuranceAuthorization, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.editAuthorization(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getUtilizationReport = createAsyncThunk(
	'/authorization/utilization/report',
	async (data: GetUtilizationReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getUtilizationReport(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const getAuthorizationReport = createAsyncThunk(
	'/authorization/authorization/report',
	async (data: GetAuthorizationReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getAuthorizationReport(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const deleteAuthorization = createAsyncThunk(
	'/authorization/delete',
	async (data: GetAvailableHoursProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.deleteAuthorization(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as { isSuccessful: boolean };
	},
);

export const getAllocationPageDataRbt = createAsyncThunk(
	'/authorization/allocation-page-data/rbt',
	async (data: GetAllocationPageDataProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getAllocationPageDataRbt(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AllocationPageDataProperties;
	},
);

export const getAllocationPageDataBcba = createAsyncThunk(
	'/authorization/allocation-page-data/bcba',
	async (data: GetAllocationPageDataProperties, thunkAPI) => {
		const response = await API.ClientAPI.InsuranceAuthorization.getAllocationPageDataBcba(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AllocationPageDataProperties;
	},
);

const insuranceAuthorizationSlice = createSlice({
	name: 'insuranceAuthorization',
	initialState,
	reducers: {
		clearActiveAuthorization(state) {
			state.authorizationDetails = initialState.authorizationDetails;
		},
	},
	extraReducers: (builder) => {
		// getInsuranceAuthorizationTypes
		builder.addCase(getInsuranceAuthorizationTypes.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getInsuranceAuthorizationTypes.fulfilled, (state, action) => {
			state.loading = false;
			state.insuranceAuthorizationTypes = action.payload;
		});
		builder.addCase(getInsuranceAuthorizationTypes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getUsersAllocationData
		builder.addCase(getUsersAllocationData.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getUsersAllocationData.fulfilled, (state, action) => {
			state.loading = false;
			state.allocationData = action.payload;
		});
		builder.addCase(getUsersAllocationData.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// deleteAuthorization
		builder.addCase(deleteAuthorization.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(deleteAuthorization.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(deleteAuthorization.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getUtilizationReport
		builder.addCase(getUtilizationReport.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getUtilizationReport.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(getUtilizationReport.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAuthorizationReport
		builder.addCase(getAuthorizationReport.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getAuthorizationReport.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(getAuthorizationReport.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getInsuranceAuthorizationSessionTypes
		builder.addCase(getInsuranceAuthorizationSessionTypes.pending, (state) => {
			// state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getInsuranceAuthorizationSessionTypes.fulfilled, (state, action) => {
			// state.loading = false;
			state.insuranceAuthorizationSessionTypes = action.payload;
		});
		builder.addCase(getInsuranceAuthorizationSessionTypes.rejected, (state, action) => {
			// state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getActiveInsuranceAuthorizations
		builder.addCase(getActiveInsuranceAuthorizations.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getActiveInsuranceAuthorizations.fulfilled, (state, action) => {
			state.loading = false;
			state.authorizationDetails = action.payload;
		});
		builder.addCase(getActiveInsuranceAuthorizations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getInsuranceAuthorizations
		builder.addCase(getInsuranceAuthorizations.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getInsuranceAuthorizations.fulfilled, (state, action) => {
			state.loading = false;
			state.allInsuranceAuthorizations = action.payload;
		});
		builder.addCase(getInsuranceAuthorizations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAllocatedHoursDetailsUser
		builder.addCase(getAllocatedHoursDetailsUser.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getAllocatedHoursDetailsUser.fulfilled, (state, action) => {
			state.loading = false;
			state.allocatedHoursDetails = action.payload;
		});
		builder.addCase(getAllocatedHoursDetailsUser.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAvailableHours
		builder.addCase(getAvailableHours.pending, (state) => {
			state.loading = true;
			state.error = initialState.error;
		});
		builder.addCase(getAvailableHours.fulfilled, (state, action) => {
			state.loading = false;
			state.availableHoursDetails = action.payload;
		});
		builder.addCase(getAvailableHours.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAllocationPageDataRbt
		builder.addCase(getAllocationPageDataRbt.pending, (state) => {
			state.allocationPageLoading = true;
		});
		builder.addCase(getAllocationPageDataRbt.fulfilled, (state, action) => {
			state.allocationPageLoading = false;
			state.allocationPageData = action.payload;
		});
		builder.addCase(getAllocationPageDataRbt.rejected, (state, action) => {
			state.allocationPageLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAllocationPageDataBcba
		builder.addCase(getAllocationPageDataBcba.pending, (state) => {
			state.allocationPageLoading = true;
		});
		builder.addCase(getAllocationPageDataBcba.fulfilled, (state, action) => {
			state.allocationPageLoading = false;
			state.allocationPageData = action.payload;
		});
		builder.addCase(getAllocationPageDataBcba.rejected, (state, action) => {
			state.allocationPageLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateAllocatedHours
		builder.addCase(updateAllocatedHours.pending, (state) => {
			state.isUpdatingAllocation = true;
		});
		builder.addCase(updateAllocatedHours.fulfilled, (state) => {
			state.isUpdatingAllocation = false;
		});
		builder.addCase(updateAllocatedHours.rejected, (state, action) => {
			state.isUpdatingAllocation = false;
			state.error = action.payload as ErrorProperties;
		});
	},
});

export const { clearActiveAuthorization } = insuranceAuthorizationSlice.actions;
export default insuranceAuthorizationSlice.reducer;
